import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Box,
    Typography,
    TextField,
    Button,
    useTheme,
} from '@mui/material';
import LoadingSpinner from '../components/LoadingSpinner';
import PasswordField from './PasswordField';
import StaticBackground from './StaticBackground';
import {
    RackletLogo,
    RackletLogov2,
    RackletLogov3,
} from '../assets/SvgIcons';
import FormContainer from './FormContainer';

const HeroCard = ({ handleLogin, showRegister, error }) => {
    const loading = useSelector((state) => state.loading);
    const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [visible, setVisible] = useState(false);
    const [logoIndex, setLogoIndex] = useState(0);
    const [showPassword, setShowPassword] = useState(false);
    const usernameRef = useRef(null);
    const theme = useTheme();

    useEffect(() => {
        usernameRef.current?.focus();
        setTimeout(() => setVisible(true), 200);
        const logoTimer = setTimeout(() => setLogoIndex(1), 2000);
        return () => clearTimeout(logoTimer);
    }, []);

    const memoizedBackground = useMemo(
        () => <StaticBackground numShapes={20} />,
        []
    );

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && email && password) {
            handleLogin(email, password);
        }
    };

    const handleLogoClick = () => {
        setLogoIndex((prevIndex) => (prevIndex + 1) % 3);
    };

    const renderLogo = () => {
        switch (logoIndex) {
            case 1:
                return <RackletLogov2 />;
            case 2:
                return <RackletLogov3 />;
            default:
                return <RackletLogo />;
        }
    };

    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'space-between',
                borderRadius: 2,
                width: '100%',
            }}
        >
            {memoizedBackground}

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-around',
                    alignItems: 'center',
                    borderRadius: 2,
                    width: '100%',
                    p: 4,
                    background:
                        theme.palette.mode === 'dark'
                            ? 'linear-gradient(to top, rgba(33,33,33,0.8), rgba(0,116,211,0.2))'
                            : 'linear-gradient(to top, rgba(255,255,255,0.8), rgba(0,116,211,0.1))',
                    transition: 'background-color 0.3s ease',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        mx: 3,
                        mb: 2,
                        mt: -2,
                        borderRadius: 2,
                    }}
                >
                    <Box
                        onClick={handleLogoClick}
                        sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                    >
                        {renderLogo()}
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            opacity: visible ? 1 : 0,
                            transition: 'opacity .5s ease-in',
                            mt: -0.5,
                        }}
                    >
                        <Typography sx={{ fontSize: 34 }}>Racklet</Typography>
                        <Typography
                            sx={{
                                fontSize: 34,
                                backgroundColor: '#66B2FF',
                                color: '#fff',
                                borderRadius: '124px',
                                ml: 1,
                                px: 1.5,
                            }}
                        >
                            AI
                        </Typography>
                    </Box>
                </Box>

                <FormContainer>
                    {loading ? (
                        <LoadingSpinner />
                    ) : isAuthenticated ? (
                        <Typography sx={{ color: 'success.main' }}>
                            You are already logged in!
                        </Typography>
                    ) : (
                        <>
                            <TextField
                                type="text"
                                placeholder="Email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                onKeyDown={handleKeyDown}
                                inputRef={usernameRef}
                                fullWidth
                                sx={{ mb: 2 }}
                            />
                            <PasswordField
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                onKeyDown={handleKeyDown}
                            />
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-evenly',
                                    width: '100%',
                                    mt: 2,
                                }}
                            >
                                <Button variant="text" onClick={showRegister}>
                                    Register
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={() => handleLogin(email, password)}
                                    disabled={!email || !password}
                                >
                                    Login
                                </Button>
                            </Box>
                            {error && (
                                <Typography sx={{ color: 'error.main', mt: 2 }}>
                                    {error}
                                </Typography>
                            )}
                        </>
                    )}
                </FormContainer>
            </Box>
        </Box>
    );
};

export default HeroCard;
